import React, { ReactElement, useEffect, useState } from 'react';
import { Loader, Text, usePerPageValueFromUrl } from 'idsk-react-components';
import { MobilePerPageSelect, Pagination, useResponsiveContext } from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import {
  defaultPerPageValue,
  perPageDataArray,
} from '../../../../constants/PaginationPerPageConstants';
import getPaginationTotalLabel from '../../../../function/getPaginationTotalLabel';
import routes from '../../../../routing/routes';
import UlohyListTable from './component/UlohyListTable';
import useMojeUlohyList from './hooks/useMojeUlohyList';
import './mojeUlohyList.scss';
import useHistoryPageContext
  from '../../../../context/BreadcrumbContext/hook/useHistoryPageContext';
import MojeUlohyPanel from './component/MojeUlohyPanel';

interface Props {
  setPageStatus?: (status: number) => void;
}

const MojeUlohyList = ({ setPageStatus }: Props): ReactElement => {
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSmallScreen } = useResponsiveContext();
  const [ibaMojeLegProcesy, setIbaMojeLegProcesy] = useState(false);
  const {
    legislativnyMaterialPage,
    pageNumber,
    requestStatus,
    searchText,
    reloadMaterialPage,
  } = useMojeUlohyList(ibaMojeLegProcesy);

  const { setHistoryPages } = useHistoryPageContext();
  useEffect(() => {
    setHistoryPages([{ to: routes.mojeUlohy, heading: 'Moje úlohy' }]);
  }, [setHistoryPages]);

  useEffect(() => {
    setPageStatus(requestStatus.responseCode);
  }, [requestStatus.responseCode, setPageStatus]);

  if (!legislativnyMaterialPage) {
    return <Loader loading={requestStatus.loading} />;
  }

  if (legislativnyMaterialPage.totalElements === 0 && !searchText) {
    return (
      <>
        <MojeUlohyPanel
          searchText={searchText}
          ibaMojeLegProcesy={ibaMojeLegProcesy}
          setIbaMojeLegProcesy={setIbaMojeLegProcesy}
        />
        <Loader loading={requestStatus.loading}>
          <Text type="p" size="l" className="text-center">
            V súčasnosti nemáte priradené žiadne úlohy.
          </Text>
        </Loader>
      </>
    );
  }

  if (legislativnyMaterialPage.totalElements === 0) {
    return (
      <>
        <MojeUlohyPanel
          searchText={searchText}
          ibaMojeLegProcesy={ibaMojeLegProcesy}
          setIbaMojeLegProcesy={setIbaMojeLegProcesy}
        />
        <Loader loading={requestStatus.loading}>
          <Text type="p" size="l" className="text-center">
            Nenašli sa žiadne záznamy.
          </Text>
        </Loader>
      </>
    );
  }

  return (
    <div className="moje-ulohy-list">
      <MojeUlohyPanel
        searchText={searchText}
        ibaMojeLegProcesy={ibaMojeLegProcesy}
        setIbaMojeLegProcesy={setIbaMojeLegProcesy}
      />
      {perPageDataArray.length > 0 && (
        <>
          {isSmallScreen ? (
            <div className="per-page-select">
              <MobilePerPageSelect
                onRecordsPerPageChange={(value) => {
                  const updatedSearchParams = new URLSearchParams(searchParams.toString());
                  updatedSearchParams.set('perPage', value.toString());
                  setSearchParams(updatedSearchParams.toString());
                }}
                recordsPerPage={perPageDataArray}
                perPageValue={perPageValue}
                totalText={getPaginationTotalLabel(legislativnyMaterialPage, pageNumber, 'Úlohy')}
              />
            </div>
          ) : (
            <Text className="pagination-total-label">
              {getPaginationTotalLabel(legislativnyMaterialPage, pageNumber, 'Úlohy')}
            </Text>
          )}
        </>
      )}
      <Loader loading={requestStatus.loading}>
        <>
          {legislativnyMaterialPage && (
            <UlohyListTable
              ulohyPage={legislativnyMaterialPage}
              reloadUlohyPage={reloadMaterialPage}
            />
          )}
          <div className="moje-ulohy-list-pagination-row">
            <Pagination
              hideTotal
              number={pageNumber}
              size={legislativnyMaterialPage.size}
              totalElements={legislativnyMaterialPage.totalElements}
              onRecordsPerPageChange={(value) => {
                const updatedSearchParams = new URLSearchParams(searchParams.toString());
                updatedSearchParams.set('perPage', value.toString());
                setSearchParams(updatedSearchParams.toString());
              }}
              recordsPerPage={perPageDataArray}
              perPageValue={perPageValue}
            />
          </div>
        </>
      </Loader>
    </div>
  );
};

export default MojeUlohyList;
